import { message } from 'antd';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import useDatasetStore from '../../../../store/datasetStore';
import {
  IBestDataset,
  IPlotVariable,
  RosePlotChart,
} from '../../../../types/analytics';
import { IGetRosePlotV2ApiPayload } from '../../../../types/chart';
import { MarkerItem } from '../../../../types/home';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { IDatasetBrief, IEnrichedMetadata } from '../../../../types/metadata';
import { formatDate, getPointNameAndAcronym } from '../../../../utils/common';
import { SplitCoordinates } from '../../../../utils/coordinate';
import { isDatasetOfflineFromBriefs } from '../../../../utils/offlineDatasetGuard';
import { DT0Format } from '../../../../utils/StaticVariable';

interface UseRosePlotParams {
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  datasets: IBestDataset[];
  defaultDirectionShortName?: string;
  defaultValueShortName?: string;
  variables: IPlotVariable[];
  dateRange: string[];
  plotData?: RosePlotChart;
  plotDefaultInterval: number;
  updatedRosePlotData: RosePlotChart | null;
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  point?: MarkerItem;
  onUpdateRosePlot: (values: IGetRosePlotV2ApiPayload) => void;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  setUpdatedRosePlotData: (item: any) => void;
  onDatasetChange: (item: string) => void;
  enrichedMetadata: IEnrichedMetadata[];
  coordinates: number[];
  errorUpdate: string;
}

export const useRosePlotParams = (params: UseRosePlotParams) => {
  const {
    previewDatasetId,
    variables,
    previewDatasetTimeRange,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
    datasets,
    defaultDirectionShortName,
    defaultValueShortName,
    dateRange,
    plotData,
    plotDefaultInterval,
    updatedRosePlotData,
    pointProps,
    userInput,
    updateUserInput,
    point,
    coordinates,
    enrichedMetadata,
    errorUpdate,
    onUpdateRosePlot: onUpdateRosePlotProp,
    setUpdatedRosePlotData,
  } = params;

  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);

  const degreeIntervals = [4, 8, 12, 16, 32, 36];

  const [rosePlotData, setRosePlotData] = useState<any[]>([]);
  const [rosePlotSeries, setRosePlotSeries] = useState<any[]>([]);
  const [directionalDataset, setDirectionalDataset] = useState(
    userInput.RosePlot.directionalDataset
  );
  const [directionalDatasetIsOffline, setDirectionalDatasetIsOffline] =
    useState<boolean>(false);
  const [month, setMonth] = useState(userInput.RosePlot.month);
  const [variable, setVariable] = useState<IPlotVariable | null>(
    userInput.RosePlot.variable
  );
  const [variablesX, setVariablesX] = useState<IPlotVariable[] | null>(
    userInput.RosePlot.variablesX
  );
  const [direction, setDirection] = useState<IPlotVariable | null>(
    userInput.RosePlot.direction
  );
  const [directions, setDirections] = useState<IPlotVariable[] | null>(
    userInput.RosePlot.variablesY
  );
  const [legendInterval, setLegendInterval] = useState(
    userInput.RosePlot.legendInterval
  );
  const [directionNo, setDirectionNo] = useState(
    userInput.RosePlot.directionNo
  );
  const [directionChartNo, setDirectionChartNo] = useState(16);
  const [startDate, setStartDate] = useState(
    userInput?.RosePlot?.startDate ??
      moment(previewDatasetTimeRange[1]).subtract(1, 'years')
  );
  const [endDate, setEndDate] = useState(
    userInput?.RosePlot?.endDate ?? previewDatasetTimeRange[1]
  );
  const [unitX, setUnitX] = useState('');
  const [previewsettingChanged, setPreviewsettingChanged] = useState(false);
  const [downloadName, setDownloadName] = useState<string | undefined>();
  const selfTriggered = useRef(false);

  useEffect(() => {
    updateUserInput({
      RosePlot: {
        dataset: previewDatasetId,
        month: month,
        variable: variable,
        variablesX: variablesX,
        variablesY: directions,
        startDate: startDate,
        endDate: endDate,
        direction: direction,
        directionNo: directionNo,
        legendInterval: legendInterval,
        directionalDataset: directionalDataset,
      },
    });
  }, [
    previewDatasetId,
    variable,
    variablesX,
    startDate,
    endDate,
    direction,
    directionNo,
    legendInterval,
    directions,
    directionalDataset,
    updateUserInput,
    month,
  ]);

  const doDatasetDirectionalChange = useCallback(
    async (active: string) => {
      if (!enrichedMetadata) return;

      const isOffline = isDatasetOfflineFromBriefs(active, datasetBriefs);
      setDirectionalDataset(active);
      setDirectionalDatasetIsOffline(isOffline);

      if (isOffline) {
        return;
      }

      selfTriggered.current = true;

      let metadata = enrichedMetadata.find(
        (md: IEnrichedMetadata) =>
          md.DatasetId === active && md.CoordinatesString === coordinates.join()
      );

      if (!metadata) {
        metadata = await Api.metadata.getMetadata(active);
      }

      if (!metadata) return;

      const withoutTime = metadata.ItemsInfo.filter(
        (item: IPlotVariable) =>
          item.Name !== 'Time' && item.Name.includes('Direction')
      );
      const defaultVariableY = withoutTime.find(
        (v: IPlotVariable) => v.ShortName === defaultDirectionShortName
      );
      setPreviewsettingChanged(true);
      setDirectionalDataset(active);
      setDirections(withoutTime);
      setDirection(defaultVariableY ? defaultVariableY : withoutTime[0]);
    },
    [enrichedMetadata, coordinates, datasetBriefs]
  );

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setPreviewsettingChanged(true);
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setPreviewsettingChanged(true);
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doChangeDirectionNo = useCallback(
    (active: number | string | undefined) => {
      if (active !== undefined && !isNaN(Number(active))) {
        selfTriggered.current = true;
        setPreviewsettingChanged(true);
        setDirectionNo(Number(active));
      }
    },
    []
  );

  const doChangeIntervalNo = useCallback(
    (active: number | string | null | undefined) => {
      if (active !== undefined && !isNaN(Number(active))) {
        selfTriggered.current = true;
        setPreviewsettingChanged(true);
        setLegendInterval(Number(active));
      }
    },
    []
  );

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      return isAfterEndDate
        ? isAfterEndDate
        : current.isBefore(previewDatasetTimeRange[0], 'day');
    },
    [endDate, previewDatasetTimeRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      return isBeforeStartDate
        ? isBeforeStartDate
        : current.isAfter(previewDatasetTimeRange[1], 'day');
    },
    [startDate, previewDatasetTimeRange]
  );

  const handleCheckboxChange = useCallback(() => {
    selfTriggered.current = true;
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
  }, [
    startDate,
    endDate,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  ]);

  const doVariableChange = useCallback(
    (active: number) => {
      if (variables.length > 0) {
        selfTriggered.current = true;
        const item = variables.find((a) => a.Index === active)!;
        if (
          item.Name === 'Current Direction' &&
          direction?.Name === 'Current Direction'
        ) {
          return;
        }
        setPreviewsettingChanged(true);
        setVariable(item);
      }
    },
    [variables, direction]
  );

  const doMonthChange = useCallback((value: number) => {
    setPreviewsettingChanged(true);
    setMonth(value);
  }, []);

  const doDirectionChange = useCallback(
    (active: number) => {
      if (directions && directions.length > 0) {
        selfTriggered.current = true;
        const item = variables.find((a) => a.Index === active)!;
        if (
          item.Name === 'Current Direction' &&
          direction?.Name === 'Current Direction'
        ) {
          return;
        }
        setPreviewsettingChanged(true);
        setDirection(item);
      }
    },
    [directions, variables, variable]
  );

  const getRoseDataPrepare = useCallback(
    (rosePlotChart: RosePlotChart) => {
      if (
        variable === null ||
        direction === null ||
        rosePlotChart?.Data === null ||
        rosePlotChart?.Axises === null
      ) {
        return;
      }
      const axisX = rosePlotChart.Axises.find((axis) => axis.Type === 'X');
      setUnitX(axisX !== undefined ? axisX.Unit : '');
      const axisY = rosePlotChart.Axises.find((axis) => axis.Type === 'Y');
      const unitY = axisY !== undefined ? ' ' + axisY.Unit : '';
      const roseSeries = rosePlotChart.Ranges!.map((r) => r.Name + unitY);

      const roseData = Object.keys(rosePlotChart.Data!).map((category) => {
        const dataSet: { Name: string; Frequency: number }[] = (
          rosePlotChart.Data as any
        )[category];
        return dataSet.reduce(
          (a, b) => {
            return { ...a, [b.Name + unitY]: b.Frequency };
          },
          { category }
        );
      });
      setRosePlotSeries(roseSeries);
      setRosePlotData(roseData);
      setUpdatedRosePlotData(rosePlotChart);
    },
    [variable, direction, setUpdatedRosePlotData]
  );

  const onUpdateRosePlot = useCallback(
    (dateRange?: any, directionNumber?: number, defaultInterval?: number) => {
      if (variable) {
        setDownloadName(
          getPointNameAndAcronym(previewDatasetId, datasetBriefs, point) +
            pointProps +
            '_' +
            formatDate(startDate) +
            '_' +
            formatDate(endDate) +
            '_Rose_Plot'
        );
        const brief = datasetBriefs.find(
          (brief: IDatasetBrief) => brief.id === previewDatasetId
        );
        const isFwe = brief?.isFWE;
        const orderId = enrichedMetadata.find(
          (item) =>
            item.CoordinatesString === coordinates.join(',') &&
            item.DatasetId === previewDatasetId
        )?.OrderId;
        const values: IGetRosePlotV2ApiPayload = {
          datasetName: previewDatasetId,
          parameters: '',
          startDate: dateRange
            ? dateRange[0].format(DT0Format)
            : startDate.format(DT0Format),
          endDate: dateRange
            ? dateRange[1].format(DT0Format)
            : endDate.format(DT0Format),
          degreeInterval: directionNumber ?? directionNo,
          legendInterval: defaultInterval ?? legendInterval,
          valueColumnName: variable.Name,
          directionColumnName: direction?.Name,
          directionalDataset: directionalDataset,
          month: month,
          isFwe: isFwe,
          orderId: orderId,
        };
        onUpdateRosePlotProp(values);
      }
    },
    [
      startDate,
      endDate,
      previewDatasetId,
      month,
      directionalDataset,
      variable,
      legendInterval,
      direction,
      directionNo,
      datasetBriefs,
      point,
      pointProps,
      onUpdateRosePlotProp,
      enrichedMetadata,
      coordinates,
    ]
  );

  useEffect(() => {
    if (!previewsettingChanged && dateRange?.length > 1) {
      setDownloadName(
        getPointNameAndAcronym(previewDatasetId, datasetBriefs, point) +
          pointProps +
          '_' +
          formatDate(moment(dateRange[0])) +
          '_' +
          formatDate(moment(dateRange[1])) +
          '_Rose_Plot'
      );
    }
  }, [
    dateRange,
    previewsettingChanged,
    pointProps,
    previewDatasetId,
    datasetBriefs,
    point,
  ]);

  useEffect(() => {
    if (
      !isEmpty(variables) &&
      updatedRosePlotData === null &&
      errorUpdate === ''
    ) {
      setDirectionalDataset(previewDatasetId);
      setDirectionalDatasetIsOffline(
        isDatasetOfflineFromBriefs(previewDatasetId, datasetBriefs)
      );
      if (userInput.RosePlot.startDate && userInput.RosePlot.endDate) {
        setStartDate(userInput.RosePlot.startDate);
        setEndDate(userInput.RosePlot.endDate);
      } else {
        setStartDate(moment(previewDatasetTimeRange[1]).subtract(1, 'years'));
        setEndDate(moment(previewDatasetTimeRange[1]));
      }
      const variableList = variables.filter(
        (item) =>
          item.Name !== 'Time' &&
          !item.Name.includes('Direction') &&
          !item.Name.includes('Water Level')
      );
      if (
        defaultValueShortName &&
        defaultDirectionShortName &&
        variableList.length > 0
      ) {
        const directionsList = variables.filter(
          (item) =>
            item.Name !== 'Time' &&
            item.Name.includes('Direction') &&
            !item.Name.includes('Water Level')
        );
        setVariablesX(variables);
        setDirections(directionsList);
        const restoredVariable = userInput.RosePlot.variable
          ? variableList.find(
              (v) => v.Index === userInput.RosePlot.variable?.Index
            )
          : undefined;
        const defaultVariableX = variableList.find(
          (v: IPlotVariable) => v.ShortName === defaultValueShortName
        );
        setVariable(restoredVariable ?? defaultVariableX ?? variableList[0]);

        const restoredDirection = userInput.RosePlot.direction
          ? directionsList.find(
              (v) => v.Index === userInput.RosePlot.direction?.Index
            )
          : undefined;
        const defaultVariableY = directionsList.find(
          (v: IPlotVariable) => v.ShortName === defaultDirectionShortName
        );
        setDirection(restoredDirection ?? defaultVariableY ?? directionsList[0]);
      }
    }
  }, [
    variables,
    updatedRosePlotData,
    defaultValueShortName,
    defaultDirectionShortName,
    errorUpdate,
  ]);

  useEffect(() => {
    if (previewsettingChanged) {
      message.warning('Please click Update once you finished your selection');
    }
  }, [previewsettingChanged]);

  useEffect(() => {
    if (
      plotDefaultInterval &&
      updatedRosePlotData === null &&
      !isNaN(Number(plotDefaultInterval))
    ) {
      setLegendInterval(plotDefaultInterval);
    }
  }, [plotDefaultInterval, updatedRosePlotData]);

  useEffect(() => {
    if (plotData?.Directions && updatedRosePlotData === null) {
      setDirectionNo(plotData.Directions.length);
    }
  }, [plotData?.Directions, updatedRosePlotData]);

  useEffect(() => {
    if (plotData && variable && direction) {
      getRoseDataPrepare(plotData);
    }
  }, [plotData, variable, direction, getRoseDataPrepare]);

  useEffect(() => {
    if (updatedRosePlotData !== null) {
      getRoseDataPrepare(updatedRosePlotData);
      updatedRosePlotData?.Directions &&
        setDirectionChartNo(updatedRosePlotData?.Directions?.length);
    } else {
      setRosePlotData([]);
      setRosePlotSeries([]);
    }
  }, [updatedRosePlotData, getRoseDataPrepare]);

  useEffect(() => {
    if (isForAllAnalytics && variable && directionNo && legendInterval) {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateRosePlot(globalDateRange, directionNo, legendInterval);
        setStartDate(globalDateRange[0]);
        setEndDate(globalDateRange[1]);
      }
    }
  }, [
    isForAllAnalytics,
    variable,
    directionNo,
    legendInterval,
    globalDateRange,
  ]);

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [datasets]);

  const selectedDatasetInfo = useMemo(() => {
    return datasets.find((d) => d.id === previewDatasetId);
  }, [datasets, previewDatasetId]);

  const memoCoords = useMemo(() => {
    return SplitCoordinates(pointProps);
  }, [pointProps]);

  return {
    rosePlotData,
    rosePlotSeries,
    variable,
    direction,
    variablesX,
    variables,
    directions,
    legendInterval,
    directionNo,
    directionChartNo,
    startDate,
    endDate,
    unitX,
    downloadName,
    degreeIntervals,
    memoCoords,
    categories,
    selectedDatasetInfo,
    doStartDateChange,
    doEndDateChange,
    doChangeDirectionNo,
    doChangeIntervalNo,
    handleCheckboxChange,
    doVariableChange,
    doDirectionChange,
    onUpdateRosePlot,
    disabledStartDate,
    disabledEndDate,
    directionalDataset,
    directionalDatasetIsOffline,
    doDatasetDirectionalChange,
    doMonthChange,
    month,
  };
};
